import React, { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { BottomNav } from './BottomNav';
import { churchAPI } from '../../lib/api';
import { useModuleTracker } from '../../hooks/useModuleTracker';
import { useAuth } from '../../context/AuthContext';
import { Sheet, SheetContent } from '../ui/sheet';
import { BillingBanner } from '../BillingBanner';

export function AppShell() {
  const { isSuperAdmin, selectedChurchId, user } = useAuth();
  useModuleTracker(selectedChurchId, !!user);
  const [churches, setChurches] = useState([]);
  const [churchesLoading, setChurchesLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [churchInfo, setChurchInfo] = useState(null);

  // El listado viene del Hub: si falla se reintenta, para que el selector nunca quede vacío en silencio.
  const retryTimer = React.useRef(null);
  const loadChurches = useCallback(async (retries = 2) => {
    clearTimeout(retryTimer.current);
    setChurchesLoading(true);
    try {
      const res = await churchAPI.list();
      setChurches(res.data?.data || []);
      setChurchesLoading(false);
    } catch {
      if (retries > 0) {
        retryTimer.current = setTimeout(() => loadChurches(retries - 1), 2500);
        return;  // sigue en "Cargando…" mientras queden reintentos
      }
      setChurchesLoading(false);
    }
  }, []);

  useEffect(() => () => clearTimeout(retryTimer.current), []);

  useEffect(() => {
    if (isSuperAdmin) loadChurches();
  }, [isSuperAdmin, loadChurches]);

  useEffect(() => {
    if (!selectedChurchId) {
      setChurchInfo(null);
      return;
    }
    // Instant render from the login payload, then refine with the full record
    setChurchInfo({ id: selectedChurchId, name: user?.church_name, logoUrl: user?.church_logo_url });
    churchAPI.get(selectedChurchId).then(res => {
      const data = res.data?.data;
      if (data) {
        setChurchInfo({ id: data.id, name: data.name, logoUrl: data.logo_url });
      }
    }).catch(() => {});
  }, [selectedChurchId, user?.church_name, user?.church_logo_url]);

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar churches={churches} churchInfo={churchInfo}
        churchesLoading={churchesLoading} onReloadChurches={loadChurches} />

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-[280px] bg-[#062B36] text-white border-white/10 overflow-y-auto" data-testid="mobile-menu-sheet"
          onClick={(e) => { if (e.target.closest('a')) setMobileMenuOpen(false); }}>
          <Sidebar churches={churches} churchInfo={churchInfo}
            churchesLoading={churchesLoading} onReloadChurches={loadChurches} inSheet />
        </SheetContent>
      </Sheet>

      <main className="flex-1 min-h-screen pb-20 lg:pb-0">
        <BillingBanner />
        {/* Al cambiar de congregación se remonta la página para que recargue sus datos */}
        <Outlet key={selectedChurchId || 'all'} context={{ setMobileMenuOpen, churchInfo }} />
      </main>

      <BottomNav />
    </div>
  );
}

export default AppShell;
